import { useCallback, useEffect, useRef, useState } from "react";

const YIELD_MS = 50;

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

export interface UseNeedsRefetchUntilOptions<TItem> {
  refetch: () => Promise<{ data?: TItem[] } | undefined>;
  isFetching: boolean;
  maxAttempts: number;
  shouldStop: (list: TItem[], submittedNeedId: string) => boolean;
  onSuccess?: (submittedNeedId: string) => void;
}

export interface UseNeedsRefetchUntilResult {
  isPolling: boolean;
  isExpectedStatusNotReceived: boolean;
  startPolling: (submittedNeedId: string | undefined) => void;
  clearExpectedStatusError: () => void;
}

export function useNeedsRefetchUntil<TItem>({
  refetch,
  isFetching,
  maxAttempts,
  shouldStop,
  onSuccess,
}: UseNeedsRefetchUntilOptions<TItem>): UseNeedsRefetchUntilResult {
  const [isPolling, setIsPolling] = useState(false);
  const [isExpectedStatusNotReceived, setIsExpectedStatusNotReceived] = useState(false);
  const isFetchingRef = useRef(isFetching);

  useEffect(() => {
    isFetchingRef.current = isFetching;
  }, [isFetching]);

  const waitUntilNotFetching = useCallback(async () => {
    while (isFetchingRef.current) {
      await sleep(YIELD_MS);
    }
  }, []);

  const startPolling = useCallback(
    (submittedNeedId: string | undefined) => {
      if (!submittedNeedId) {
        return;
      }

      setIsExpectedStatusNotReceived(false);
      void (async () => {
        setIsPolling(true);
        let reachedExpectedStatus = false;
        let refetchFailed = false;
        try {
          for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
            await waitUntilNotFetching();

            let result: { data?: TItem[] } | undefined;
            try {
              result = await refetch();
            } catch {
              refetchFailed = true;
              break;
            }

            const list = result?.data ?? [];
            if (shouldStop(list, submittedNeedId)) {
              reachedExpectedStatus = true;
              onSuccess?.(submittedNeedId);
              break;
            }
          }
          if (!reachedExpectedStatus && !refetchFailed) {
            setIsExpectedStatusNotReceived(true);
          }
        } finally {
          setIsPolling(false);
        }
      })();
    },
    [refetch, maxAttempts, shouldStop, onSuccess, waitUntilNotFetching]
  );

  const clearExpectedStatusError = useCallback(() => {
    setIsExpectedStatusNotReceived(false);
  }, []);

  return { isPolling, isExpectedStatusNotReceived, startPolling, clearExpectedStatusError };
}
